import type { Immutable } from "immer";
import React from "react";
import { getGlobalSpacingClasses } from "tsi-common-react/src/components/_spacing/globalSpacing";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";

import type {
    ColumnBlock,
    HomeChartBlock as HomeChartBlockValue,
} from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

const rowClass = (index: number): "even" | "odd" =>
    (index + 1) % 2 === 0 ? "even" : "odd";

const DesktopColumn = (
    props: Immutable<{ column: ColumnBlock; index: number }>,
): React.JSX.Element => {
    const outerCounter = props.index + 1;
    const columnClass =
        outerCounter === 1
            ? concatClassNames([
                  styles.prodchartCol,
                  styles.prodchartColFirstCol,
              ])
            : concatClassNames([
                  styles.prodchartCol,
                  styles.prodchartColSecondCol,
              ]);
    return (
        <div className={columnClass}>
            {(props.column.column ?? []).map((cell, i) => {
                if (i === 0) {
                    return (
                        <div
                            key={i}
                            className={concatClassNames([
                                styles.prodchartCell,
                                styles.prodchartCellFirstRow,
                            ])}
                        >
                            {cell.copy}
                        </div>
                    );
                }
                const row = rowClass(i);
                return (
                    <div
                        key={i}
                        className={concatClassNames([
                            styles.prodchartCell,
                            `prodchart__cell--${cell.class_name ?? ""}`,
                            `prodchart__cell--${row}`,
                            outerCounter === 2
                                ? `prodchart__cell--cocoon-${row}`
                                : "",
                            outerCounter === 1
                                ? styles.prodchartCellFirstCol
                                : "",
                        ])}
                    >
                        {cell.copy}
                    </div>
                );
            })}
        </div>
    );
};

const MobileColumn = (
    props: Immutable<{ column: ColumnBlock }>,
): React.JSX.Element => {
    return (
        <div
            className={concatClassNames([
                styles.prodchartCol,
                styles.prodchartColMobile,
            ])}
        >
            {(props.column.column ?? []).map((cell, i) => {
                if (i === 0) {
                    return (
                        <div
                            key={i}
                            className={concatClassNames([
                                styles.prodchartCell,
                                styles.prodchartCellFirstRow,
                            ])}
                        >
                            {cell.copy}
                        </div>
                    );
                }
                const row = rowClass(i);
                return (
                    <div
                        key={i}
                        className={concatClassNames([
                            styles.prodchartCell,
                            `prodchart__cell--${cell.class_name ?? ""}`,
                            `prodchart__cell--${row}`,
                        ])}
                    >
                        {cell.copy}
                    </div>
                );
            })}
        </div>
    );
};

export const CocoonHomeChartBlock = (
    props: Immutable<{ value: HomeChartBlockValue }>,
): React.JSX.Element => {
    const value = props.value;
    const columns = value.columns ?? [];
    const carouselRef = React.useRef<HTMLDivElement>(null);
    useFlickity(carouselRef, {
        options: {
            cellAlign: "left",
            watchCSS: true,
            pageDots: false,
        },
    });

    return (
        <section
            className={concatClassNames([
                styles.homeChartBlock,
                getGlobalSpacingClasses(value.spacing),
            ])}
        >
            <div className="l-capped-width ">
                <h1 className={styles.homeChartBlockHeader}>{value.header}</h1>
                <h3 className={styles.homeChartBlockCopy}>{value.copy}</h3>
                <div className={styles.prodchart}>
                    {columns.slice(0, 2).map((column, i) => (
                        <DesktopColumn key={i} column={column} index={i} />
                    ))}
                    <div
                        ref={carouselRef}
                        className={concatClassNames([
                            "prodchart__col-container",
                            styles.prodchartColContainer,
                        ])}
                    >
                        {columns.slice(2).map((column, i) => (
                            <MobileColumn key={i} column={column} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
